import * as XLSX from 'xlsx'
import type { MeterReadingImportRowInput, PlotReadingStatus } from '@riverpark/types'

const TEMPLATE_HEADERS = [
  'plot_old_number',
  'plot_new_number',
  'member_name',
  'previous_reading',
  'current_reading',
] as const

function escapeCsvField(value: string): string {
  if (/[",\n]/.test(value)) {
    return `"${value.replace(/"/g, '""')}"`
  }
  return value
}

function downloadBlob(blob: Blob, filename: string) {
  const url = URL.createObjectURL(blob)
  const anchor = document.createElement('a')
  anchor.href = url
  anchor.download = filename
  anchor.click()
  URL.revokeObjectURL(url)
}

function sanitizeFilenamePart(value: string): string {
  return value
    .trim()
    .replace(/\s+/g, '_')
    .replace(/[^a-zA-Z0-9_-]/g, '')
}

export function downloadMeterReadingTemplateCsv(
  utilityName: string,
  billingPeriod: string,
  plots: PlotReadingStatus[],
) {
  const lines = [
    TEMPLATE_HEADERS.join(','),
    ...plots.map((plot) =>
      [plot.plotOldNumber, plot.plotNumber, plot.memberName ?? '', '', '']
        .map((v) => escapeCsvField(String(v)))
        .join(','),
    ),
  ]
  const blob = new Blob([lines.join('\n')], { type: 'text/csv;charset=utf-8' })
  const filename = `${sanitizeFilenamePart(utilityName)}_${billingPeriod}_readings_template.csv`
  downloadBlob(blob, filename)
}

function normalizeHeader(value: unknown): string {
  return String(value ?? '')
    .trim()
    .toLowerCase()
    .replace(/\s+/g, '_')
}

export async function parseMeterReadingImportFile(
  file: File,
): Promise<MeterReadingImportRowInput[]> {
  const buffer = await file.arrayBuffer()
  const workbook = XLSX.read(buffer, { type: 'array' })
  const sheetName = workbook.SheetNames[0]
  if (!sheetName) throw new Error('The file has no worksheets')

  const sheet = workbook.Sheets[sheetName]
  if (!sheet) throw new Error('Could not read the worksheet')

  const rows = XLSX.utils.sheet_to_json<unknown[]>(sheet, { header: 1, defval: '' })
  if (rows.length < 2) {
    throw new Error('The file must include a header row and at least one plot row')
  }

  const headerRow = rows[0]
  if (!Array.isArray(headerRow)) throw new Error('Could not read the header row')
  const headers = headerRow.map(normalizeHeader)

  const plotNewNumberIdx = headers.indexOf('plot_new_number')
  const previousReadingIdx = headers.indexOf('previous_reading')
  const currentReadingIdx = headers.indexOf('current_reading')

  if (plotNewNumberIdx < 0 || previousReadingIdx < 0 || currentReadingIdx < 0) {
    throw new Error(
      'The file must include plot_new_number, previous_reading, and current_reading columns — use the downloaded template',
    )
  }

  const dataRows = rows
    .slice(1)
    .filter((row) => Array.isArray(row) && row.some((cell) => String(cell ?? '').trim() !== ''))

  return dataRows.map((row) => {
    const cells = row as unknown[]
    return {
      rowId: crypto.randomUUID(),
      plotNewNumber: String(cells[plotNewNumberIdx] ?? '').trim(),
      previousReading: String(cells[previousReadingIdx] ?? '').trim(),
      currentReading: String(cells[currentReadingIdx] ?? '').trim(),
    }
  })
}
